import _createForOfIteratorHelper from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createForOfIteratorHelper";
import _classCallCheck from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import _inherits from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/inherits";
import _createSuper from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createSuper";
/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
/**
 * @module image/imagecaption/imagecaptionediting
 */
import { Plugin } from 'ckeditor5/src/core.js';
import { Element, enablePlaceholder } from 'ckeditor5/src/engine.js';
import { toWidgetEditable } from 'ckeditor5/src/widget.js';
import ToggleImageCaptionCommand from './toggleimagecaptioncommand.js';
import ImageUtils from '../imageutils.js';
import ImageCaptionUtils from './imagecaptionutils.js';
/**
 * The image caption engine plugin. It is responsible for:
 *
 * * registering converters for the caption element,
 * * registering converters for the caption model attribute,
 * * registering the {@link module:image/imagecaption/toggleimagecaptioncommand~ToggleImageCaptionCommand `toggleImageCaption`} command.
 */
var ImageCaptionEditing = /*#__PURE__*/function (_Plugin) {
  _inherits(ImageCaptionEditing, _Plugin);
  var _super = _createSuper(ImageCaptionEditing);
  /**
   * @inheritDoc
   */
  function ImageCaptionEditing(editor) {
    var _this;
    _classCallCheck(this, ImageCaptionEditing);
    _this = _super.call(this, editor);
    _this._savedCaptionsMap = new WeakMap();
    return _this;
  }
  /**
   * @inheritDoc
   */
  _createClass(ImageCaptionEditing, [{
    key: "init",
    value: function init() {
      var editor = this.editor;
      var schema = editor.model.schema;
      // Schema configuration.
      if (!schema.isRegistered('caption')) {
        schema.register('caption', {
          allowIn: 'imageBlock',
          allowContentOf: '$block',
          isLimit: true
        });
      } else {
        schema.extend('caption', {
          allowIn: 'imageBlock'
        });
      }
      editor.commands.add('toggleImageCaption', new ToggleImageCaptionCommand(this.editor));
      this._setupConversion();
      this._setupImageTypeCommandsIntegration();
      this._registerCaptionReconversion();
    }
    /**
     * Configures conversion pipelines to support upcasting and downcasting
     * image captions.
     */
  }, {
    key: "_setupConversion",
    value: function _setupConversion() {
      var editor = this.editor;
      var _view = editor.editing.view;
      var imageUtils = editor.plugins.get('ImageUtils');
      var imageCaptionUtils = editor.plugins.get('ImageCaptionUtils');
      var t = editor.t;
      // View -> model converter for the data pipeline.
      editor.conversion.for('upcast').elementToElement({
        view: function view(element) {
          return imageCaptionUtils.matchImageCaptionViewElement(element);
        },
        model: 'caption'
      });
      // Model -> view converter for the data pipeline.
      editor.conversion.for('dataDowncast').elementToElement({
        model: 'caption',
        view: function view(modelElement, _ref) {
          var writer = _ref.writer;
          if (!imageUtils.isBlockImage(modelElement.parent)) {
            return null;
          }
          return writer.createContainerElement('figcaption');
        }
      });
      // Model -> view converter for the editing pipeline.
      editor.conversion.for('editingDowncast').elementToElement({
        model: 'caption',
        view: function view(modelElement, _ref2) {
          var writer = _ref2.writer;
          if (!imageUtils.isBlockImage(modelElement.parent)) {
            return null;
          }
          var figcaptionElement = writer.createEditableElement('figcaption');
          writer.setCustomProperty('imageCaption', true, figcaptionElement);
          figcaptionElement.placeholder = t('Enter image caption');
          enablePlaceholder({
            view: _view,
            element: figcaptionElement,
            keepOnFocus: true
          });
          var imageAlt = modelElement.parent.getAttribute('alt');
          var label = imageAlt ? t('Caption for image: %0', [imageAlt]) : t('Caption for the image');
          return toWidgetEditable(figcaptionElement, writer, {
            label: label
          });
        }
      });
    }
    /**
     * Integrates with {@link module:image/image/imagetypecommand~ImageTypeCommand image type commands}
     * to make sure the caption is preserved when the type of an image changes so it can be restored
     * in the future if the user decides they want their caption back.
     */
  }, {
    key: "_setupImageTypeCommandsIntegration",
    value: function _setupImageTypeCommandsIntegration() {
      var _this2 = this;
      var editor = this.editor;
      var imageUtils = editor.plugins.get('ImageUtils');
      var imageCaptionUtils = editor.plugins.get('ImageCaptionUtils');
      var imageTypeInlineCommand = editor.commands.get('imageTypeInline');
      var imageTypeBlockCommand = editor.commands.get('imageTypeBlock');
      var handleImageTypeChange = function handleImageTypeChange(evt) {
        // The image type command execution can be unsuccessful.
        if (!evt.return) {
          return;
        }
        var _evt$return = evt.return,
          oldElement = _evt$return.oldElement,
          newElement = _evt$return.newElement;
        /* istanbul ignore if: paranoid check -- @preserve */
        if (!oldElement) {
          return;
        }
        if (imageUtils.isBlockImage(oldElement)) {
          var oldCaptionElement = imageCaptionUtils.getCaptionFromImageModelElement(oldElement);
          // If the old element was a captioned block image (the caption was visible),
          // simply save it so it can be restored.
          if (oldCaptionElement) {
            _this2._saveCaption(newElement, oldCaptionElement);
            return;
          }
        }
        var savedOldElementCaption = _this2._getSavedCaption(oldElement);
        // If either:
        //
        // * the block image didn't have a visible caption,
        // * the block image caption was hidden (and already saved),
        // * the inline image was passed
        //
        // just try to "pass" the saved caption from the old image to the new image
        // so it can be retrieved in the future if the user wants it back.
        if (savedOldElementCaption) {
          // Note: Since we're writing to a WeakMap, we don't bother with removing the
          // [ oldElement, savedOldElementCaption ] pair from it.
          _this2._saveCaption(newElement, savedOldElementCaption);
        }
      };
      // Presence of the commands depends on the Image(Inline|Block)Editing plugins loaded in the editor.
      if (imageTypeInlineCommand) {
        this.listenTo(imageTypeInlineCommand, 'execute', handleImageTypeChange, {
          priority: 'low'
        });
      }
      if (imageTypeBlockCommand) {
        this.listenTo(imageTypeBlockCommand, 'execute', handleImageTypeChange, {
          priority: 'low'
        });
      }
    }
    /**
     * Returns the saved {@link module:engine/model/element~Element#toJSON JSONified} caption
     * of an image model element.
     *
     * See {@link #_saveCaption}.
     *
     * @internal
     * @param imageModelElement The model element the caption should be returned for.
     * @returns The model caption element or `null` if there is none.
     */
  }, {
    key: "_getSavedCaption",
    value: function _getSavedCaption(imageModelElement) {
      var jsonObject = this._savedCaptionsMap.get(imageModelElement);
      return jsonObject ? Element.fromJSON(jsonObject) : null;
    }
    /**
     * Saves a {@link module:engine/model/element~Element#toJSON JSONified} caption for
     * an image element to allow restoring it in the future.
     *
     * A caption is saved every time it gets hidden and/or the type of an image changes. The
     * user should be able to restore it on demand.
     *
     * **Note**: The caption cannot be stored in the image model element attribute because,
     * for instance, when the model state propagates to collaborators, the attribute would get
     * lost (mainly because it does not convert to anything when the caption is hidden) and
     * the states of collaborators' models would de-synchronize causing numerous issues.
     *
     * See {@link #_getSavedCaption}.
     *
     * @internal
     * @param imageModelElement The model element the caption is saved for.
     * @param caption The caption model element to be saved.
     */
  }, {
    key: "_saveCaption",
    value: function _saveCaption(imageModelElement, caption) {
      this._savedCaptionsMap.set(imageModelElement, caption.toJSON());
    }
    /**
     * Reconverts image caption when image alt attribute changes.
     * The change of alt attribute is reflected in caption's aria-label attribute.
     */
  }, {
    key: "_registerCaptionReconversion",
    value: function _registerCaptionReconversion() {
      var editor = this.editor;
      var model = editor.model;
      var imageUtils = editor.plugins.get('ImageUtils');
      var imageCaptionUtils = editor.plugins.get('ImageCaptionUtils');
      model.document.on('change:data', function () {
        var changes = model.document.differ.getChanges();
        var _iterator = _createForOfIteratorHelper(changes),
          _step;
        try {
          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            var change = _step.value;
            if (change.attributeKey !== 'alt') {
              continue;
            }
            var image = change.range.start.nodeAfter;
            if (imageUtils.isBlockImage(image)) {
              var caption = imageCaptionUtils.getCaptionFromImageModelElement(image);
              if (!caption) {
                return;
              }
              editor.editing.reconvertItem(caption);
            }
          }
        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }
      });
    }
  }], [{
    key: "requires",
    get:
    /**
     * @inheritDoc
     */
    function get() {
      return [ImageUtils, ImageCaptionUtils];
    }
    /**
     * @inheritDoc
     */
  }, {
    key: "pluginName",
    get: function get() {
      return 'ImageCaptionEditing';
    }
  }]);
  return ImageCaptionEditing;
}(Plugin);
export { ImageCaptionEditing as default };